/**
 * personalWorkspace — the client side of first-click activation
 * (2026-08-19 owner directive, lazy personal workspace).
 *
 * A dormant "[First]'s Workspace" row (header pill, Organization page) does
 * not SWITCH on click — it ACTIVATES: the server mints structure + the
 * uniform trial (activatePersonalWorkspace, no VM inside the callable), the
 * verified switch points the back-pointer at the uid workspace, and a HARD
 * navigation lands on /onboarding — the same uniform staged flow every
 * workspace onboards through, while warmWorkspaceVm boots the VM in the
 * background. Hard navigation honors the reload-through contract every
 * workspace switch obeys (module-level accountId caches re-key only on a
 * fresh load), and going straight to /onboarding makes the first-click
 * outcome deterministic instead of relying on the gate redirect.
 *
 * Failure contract: rejects so callers can re-enable their row and show
 * their own fixed copy — callable error text is never rendered.
 */
import { activatePersonalWorkspace, switchActiveWorkspace } from './rbacService';
import { clearWorkspaceOptionsCache } from './workspaceOptionsCache';

/**
 * Navigation-free activation core (2026-08-19 "couldn't switch" incident):
 * the sealed-switch controller (workspaceTransition) needs the activate +
 * verified-switch + cache-clear sequence WITHOUT the hard navigation, so it
 * can write its own arrival markers first and own the destination. The
 * old navigating wrapper (activateAndEnterPersonalWorkspace) was DELETED
 * 2026-08-20 — every dormant click now rides beginWorkspaceTransition, and
 * a live bypass helper is exactly how the unsealed-switch inconsistency
 * would creep back in.
 */
export async function activatePersonalWorkspaceCore(uid: string): Promise<void> {
  if (!uid) throw new Error('missing uid');
  await activatePersonalWorkspace();
  await switchActiveWorkspace(uid);
  try { clearWorkspaceOptionsCache(); } catch { /* cache only */ }
  // W-R2 (owner ruling 2026-08-25): the personal workspace owns a VM —
  // kick it the moment activation lands (fire-and-forget; the endpoint
  // admits the caller's own uid once nativePosture is 'active', and the
  // boot gate + hourly reconcile sweep are the safety nets).
  void kickPersonalWorkspaceVm(uid);
}

/** Fire-and-forget personal-workspace VM provision (W-R2, 2026-08-25). */
async function kickPersonalWorkspaceVm(uid: string): Promise<void> {
  try {
    const { getAuth } = await import('firebase/auth');
    const token = await getAuth().currentUser?.getIdToken();
    if (!token) return;
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
    await fetch(`https://us-central1-${projectId}.cloudfunctions.net/provisionUserVM`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ accountId: uid }),
    });
  } catch { /* best-effort — the reconcile sweep is the backstop */ }
}

